import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useEditableContent from "../hooks/useEditableContent";

const FALLBACK_DESTINATION_IMGS = [
  "https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?w=600&q=80",
  "https://images.unsplash.com/photo-1519817650390-64a93db51149?w=600&q=80",
  "https://images.unsplash.com/photo-1539650116574-8efeb43e2750?w=600&q=80",
  "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=600&q=80",
];
const FALLBACK_PROGRAMME_IMGS = [
  "https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?w=600&q=80",
  "https://images.unsplash.com/photo-1519817650390-64a93db51149?w=600&q=80",
  "https://images.unsplash.com/photo-1539650116574-8efeb43e2750?w=600&q=80",
];

const HomePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const go = (path) => { navigate(path); window.scrollTo(0, 0); };
  const [activeTab, setActiveTab] = useState("omrah");
  const [guests, setGuests] = useState("2");
  const [date, setDate] = useState("");

  const statsValue = t("stats", { returnObjects: true });
  const destinationsValue = t("destinations", { returnObjects: true });
  const packagesValue = t("packages", { returnObjects: true });
  const whyCardsValue = t("whyCards", { returnObjects: true });
  const featureCardsValue = t("featureCards", { returnObjects: true });
  const testimonialsValue = t("testimonials", { returnObjects: true });
  const tabsValue = t("search.tabs", { returnObjects: true });

  const stats = Array.isArray(statsValue) ? statsValue : [
    { n:"12+", label:"Years Experience" },
    { n:"6000+", label:"Pilgrims Served" },
    { n:"500+", label:"Passengers / Year" },
    { n:"15+", label:"Journeys to Makkah" },
  ];
  const fallbackDestinations = Array.isArray(destinationsValue) ? destinationsValue : [
    { name:"Makkah", tag:"Holy City", discount:"20% OFF" },
    { name:"Al Madinah", tag:"Prophet's City", discount:"20% OFF" },
    { name:"Egypt", tag:"Ancient Wonder", discount:"15% OFF" },
    { name:"Turkey", tag:"East meets West", discount:"10% OFF" },
  ];
  const fallbackPackages = Array.isArray(packagesValue) ? packagesValue : [
    { title:"Makkah Spiritual Journey", loc:"Makkah + Madinah", days:14, people:"10-40", old:"14 500", price:"13 900", type:"Umrah" },
    { title:"Heart of Makkah Tour", loc:"Makkah + Madinah", days:15, people:"10-40", old:"15 800", price:"14 700", type:"Umrah Premium" },
    { title:"Egypt Discovery Tour", loc:"Cairo + Luxor + Aswan", days:10, people:"5-25", old:"9 800", price:"8 500", type:"Cultural" },
  ];
  const editableDestinations = useEditableContent("destinations.json", fallbackDestinations);
  const editableProgrammes = useEditableContent("programmes.json", fallbackPackages);
  const destinations = Array.isArray(editableDestinations) && editableDestinations.length
    ? editableDestinations.map((destination, index) => ({
      ...(fallbackDestinations[index] || {}),
      ...destination,
    }))
    : fallbackDestinations;
  const packages = Array.isArray(editableProgrammes) && editableProgrammes.length
    ? editableProgrammes
    : fallbackPackages;
  const whyCards = Array.isArray(whyCardsValue) ? whyCardsValue : [
    ["🏆", "Certified Agency", "Licensed and regulated by Moroccan authorities"],
    ["🤝", "Expert Guides", "Experienced local guides"],
    ["💰", "Best Prices", "Competitive rates with no hidden fees"],
    ["📞", "24/7 Support", "Always available for you"],
  ];
  const featureCards = Array.isArray(featureCardsValue) ? featureCardsValue : [
    ["🕌", "Hajj & Umrah Specialists", "Complete packages."],
    ["✈️", "International Ticketing", "Best routes from Morocco."],
    ["🚌", "Transportation Services", "Air-conditioned buses."],
  ];
  const testimonials = Array.isArray(testimonialsValue) ? testimonialsValue : [
    { name:"Ahmed El Fassi", city:"Casablanca", text:"Seamless Umrah journey.", rating:5 },
    { name:"Fatima Benali", city:"Rabat", text:"Perfectly organized Hajj.", rating:5 },
    { name:"Youssef Tahiri", city:"Fes", text:"Amazing Egypt tour.", rating:5 },
  ];
  const tabs = tabsValue && typeof tabsValue === "object" && !Array.isArray(tabsValue) ? tabsValue : {
    omrah: "🕌 Umrah",
    hajj: "🌙 Hajj",
    visa: "📋 Visa",
    ticket: "🎫 Ticket",
    transport: "🚌 Transport",
    intern: "🌍 International",
    service: "🛎️ Services",
  };

  return (
    <>
      <style>{`
        .hero {
          min-height:100vh;
          background:linear-gradient(to bottom,rgba(13,27,42,0.7) 0%,rgba(13,27,42,0.4) 50%,rgba(13,27,42,0.8) 100%),
            url('https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?w=1600&q=80') center/cover no-repeat;
          display:flex; flex-direction:column; align-items:center; justify-content:center;
          text-align:center; padding:120px 24px 60px; position:relative; overflow:hidden;
        }
        .hero::before { content:''; position:absolute; inset:0; background:radial-gradient(ellipse at 50% 60%,rgba(201,168,76,0.08) 0%,transparent 70%); pointer-events:none; }
        .hero-badge { background:rgba(201,168,76,0.15); border:1px solid rgba(201,168,76,0.4); color:var(--gold-light); font-size:12px; letter-spacing:3px; text-transform:uppercase; padding:8px 20px; border-radius:100px; margin-bottom:24px; display:inline-block; font-weight:600; animation:fadeUp 0.6s ease forwards; }
        .hero-title { font-family:var(--font-heading); font-size:clamp(40px,7vw,80px); color:white; line-height:1.1; margin-bottom:20px; animation:fadeUp 0.7s 0.1s ease both; }
        .hero-sub { color:rgba(255,255,255,0.8); font-size:clamp(15px,2vw,18px); line-height:1.7; max-width:580px; margin:0 auto 40px; animation:fadeUp 0.7s 0.2s ease both; }
        .hero-btns { display:flex; gap:14px; justify-content:center; flex-wrap:wrap; animation:fadeUp 0.7s 0.3s ease both; }
        .search-box { background:white; border-radius:12px; padding:28px 32px; box-shadow:0 20px 60px rgba(0,0,0,0.2); max-width:860px; width:100%; margin:40px auto 0; animation:fadeUp 0.7s 0.4s ease both; position:relative; z-index:2; }
        .search-tabs { display:flex; gap:6px; margin-bottom:24px; flex-wrap:wrap; }
        .search-tab { padding:8px 18px; border-radius:100px; border:1.5px solid var(--border); font-size:13px; font-weight:500; cursor:pointer; transition:all 0.2s; font-family:inherit; background:transparent; color:var(--text-muted); }
        .search-tab.active { background:var(--deep); color:white; border-color:var(--deep); }
        .search-fields { display:grid; grid-template-columns:1.5fr 1fr 1fr auto; gap:14px; align-items:end; }
        .search-field label { font-size:11px; font-weight:600; letter-spacing:1.5px; text-transform:uppercase; color:var(--text-muted); display:block; margin-bottom:6px; }
        .search-field input, .search-field select { width:100%; padding:12px 14px; border:1.5px solid #E8E8E8; border-radius:6px; font-family:inherit; font-size:14px; color:var(--text); transition:border-color 0.2s; outline:none; background:white; }
        .search-field input:focus, .search-field select:focus { border-color:var(--gold); }
        @media(max-width:768px) { .search-box{padding:20px;margin:32px 16px 0} .search-fields{grid-template-columns:1fr 1fr} }

        .stats-bar { background:var(--deep); padding:40px 60px; display:grid; grid-template-columns:repeat(4,1fr); gap:20px; }
        .stat-item { text-align:center; border-inline-end:1px solid rgba(255,255,255,0.08); }
        .stat-item:last-child { border:none; }
        .stat-num { font-family:var(--font-heading); font-size:36px; color:var(--gold); }
        .stat-label { font-size:13px; color:rgba(255,255,255,0.6); margin-top:4px; }
        @media(max-width:768px){ .stats-bar{padding:30px 20px;grid-template-columns:repeat(2,1fr);gap:24px} .stat-item{border:none} }

        .destinations-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:20px; }
        .dest-card { border-radius:12px; overflow:hidden; position:relative; cursor:pointer; height:320px; aspect-ratio:4/5; }
        .dest-card img { width:100%; height:100%; object-fit:cover; transition:transform 0.5s ease; display:block; }
        .dest-card:hover img { transform:scale(1.08); }
        .dest-overlay { position:absolute; inset:0; background:linear-gradient(to top,rgba(13,27,42,0.9) 0%,transparent 55%); display:flex; flex-direction:column; justify-content:flex-end; padding:20px; }
        .dest-discount-badge { position:absolute; top:14px; inset-inline-end:14px; background:var(--gold); color:white; font-size:11px; font-weight:700; padding:5px 10px; border-radius:100px; }
        .dest-tag { color:var(--gold-light); font-size:11px; letter-spacing:2px; text-transform:uppercase; margin-bottom:4px; }
        .dest-name { color:white; font-family:var(--font-heading); font-size:22px; }
        @media(max-width:768px){ .destinations-grid{grid-template-columns:repeat(2,1fr)} .dest-card{height:220px} }

        .packages-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:28px; }
        .pkg-card { border-radius:14px; overflow:hidden; background:white; box-shadow:0 4px 20px rgba(0,0,0,0.08); transition:all 0.35s; cursor:pointer; }
        .pkg-card:hover { transform:translateY(-8px); box-shadow:0 20px 50px rgba(0,0,0,0.14); }
        .pkg-img { height:220px; aspect-ratio:16/10; overflow:hidden; position:relative; background:#e7decd; }
        .pkg-img img { width:100%; height:100%; object-fit:cover; transition:transform 0.5s; display:block; }
        .pkg-card:hover .pkg-img img { transform:scale(1.05); }
        .pkg-type-badge { position:absolute; top:14px; inset-inline-start:14px; background:var(--deep); color:var(--gold); font-size:11px; font-weight:700; padding:5px 12px; border-radius:100px; }
        .pkg-body { padding:22px; }
        .pkg-meta { display:flex; gap:16px; font-size:12px; color:var(--text-muted); margin-bottom:12px; }
        .pkg-title { font-family:var(--font-heading); font-size:20px; margin-bottom:8px; color:var(--deep); }
        .pkg-location { font-size:13px; color:var(--text-muted); margin-bottom:16px; }
        .pkg-price-row { display:flex; align-items:center; justify-content:space-between; padding-top:16px; border-top:1px solid #F0F0F0; }
        .pkg-old-price { color:var(--text-muted); text-decoration:line-through; font-size:14px; }
        .pkg-new-price { font-family:var(--font-heading); font-size:24px; color:var(--gold-dark); font-weight:700; }
        @media(max-width:768px){ .packages-grid{grid-template-columns:1fr} }

        .features-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:28px; }
        .feature-card { padding:36px 28px; border-radius:14px; background:white; box-shadow:0 4px 20px rgba(0,0,0,0.06); text-align:center; transition:all 0.3s; border:1px solid transparent; }
        .feature-card:hover { border-color:var(--gold); transform:translateY(-4px); }
        .feature-icon { width:64px; height:64px; border-radius:16px; margin:0 auto 20px; background:linear-gradient(135deg,rgba(201,168,76,0.12),rgba(201,168,76,0.05)); display:flex; align-items:center; justify-content:center; font-size:28px; }
        .feature-title { font-family:var(--font-heading); font-size:20px; margin-bottom:10px; color:var(--deep); }
        .feature-text { font-size:14px; color:var(--text-muted); line-height:1.7; }
        @media(max-width:768px){ .features-grid{grid-template-columns:1fr} }

        .testi-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:24px; }
        .testi-card { background:white; border-radius:14px; padding:28px; box-shadow:0 4px 20px rgba(0,0,0,0.07); }
        .testi-stars { color:var(--gold); font-size:16px; margin-bottom:14px; }
        .testi-text { font-size:15px; color:var(--text); line-height:1.7; font-style:italic; margin-bottom:20px; }
        .testi-author { display:flex; align-items:center; gap:12px; }
        .testi-avatar { width:44px; height:44px; border-radius:50%; background:linear-gradient(135deg,var(--gold),var(--gold-dark)); display:flex; align-items:center; justify-content:center; color:white; font-family:var(--font-heading); font-size:18px; font-weight:700; flex-shrink:0; }
        @media(max-width:768px){ .testi-grid{grid-template-columns:1fr} }

        .cta-banner { background:linear-gradient(135deg,var(--deep) 0%,var(--deep2) 100%); border-radius:16px; padding:70px 60px; display:flex; align-items:center; justify-content:space-between; position:relative; overflow:hidden; }
        .cta-banner::before { content:''; position:absolute; top:-60px; inset-inline-end:-60px; width:300px; height:300px; border-radius:50%; background:radial-gradient(circle,rgba(201,168,76,0.15) 0%,transparent 70%); }
        .cta-title { font-family:var(--font-heading); font-size:clamp(24px,3.5vw,40px); color:white; margin-bottom:12px; }
        .cta-sub { color:rgba(255,255,255,0.65); font-size:15px; max-width:400px; line-height:1.6; }
        .cta-btns { display:flex; gap:14px; flex-wrap:wrap; margin-top:16px; }
        @media(max-width:768px){ .cta-banner{flex-direction:column;gap:28px;padding:40px 28px;text-align:center} }

        .why-grid { display:grid; grid-template-columns:1fr 1fr; gap:60px; align-items:center; margin-bottom:60px; }
        .why-cards { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
        .why-card { background:white; padding:22px; border-radius:12px; box-shadow:0 4px 16px rgba(0,0,0,0.06); }
        @media(max-width:768px){ .why-grid{grid-template-columns:1fr;gap:32px} }

        .home-section { padding:80px 60px; }
        @media(max-width:768px){ .home-section{padding:50px 20px} }
      `}</style>

      {/* HERO */}
      <section className="hero">
        <div className="hero-badge">{t("hero.badge")}</div>
        <h1 className="hero-title">
          {t("hero.title1")}<br />
          <span className="gold-text">{t("hero.title2")}</span>
        </h1>
        <p className="hero-sub">{t("hero.sub")}</p>
        <div className="hero-btns">
          <button className="btn-gold" onClick={() => go("/packages")}>{t("hero.explore")}</button>
          <button className="btn-outline" style={{color:"white",borderColor:"rgba(255,255,255,0.5)"}} onClick={() => go("/contact")}>{t("hero.contactUs")}</button>
        </div>

        <div className="search-box">
          <div className="search-tabs">
            {Object.entries(tabs).map(([id, label]) => (
              <button key={id} className={`search-tab ${activeTab===id?"active":""}`} onClick={() => setActiveTab(id)}>{label}</button>
            ))}
          </div>
          <div className="search-fields">
            <div className="search-field">
              <label>{t("search.label")}</label>
              <select defaultValue=""><option value="" disabled>{tabs[activeTab]}</option></select>
            </div>
            <div className="search-field">
              <label>{t("search.date")}</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="search-field">
              <label>{t("search.guests")}</label>
              <select value={guests} onChange={e => setGuests(e.target.value)}>
                {[1,2,3,4,5,6,7,8].map(n => <option key={n}>{n}</option>)}
              </select>
            </div>
            <button className="btn-gold" onClick={() => go("/packages")}>{t("search.search")}</button>
          </div>
        </div>
      </section>

      {/* STATS */}
      <div className="stats-bar">
        {stats.map(s => (
          <div key={s.n} className="stat-item">
            <div className="stat-num">{s.n}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* DESTINATIONS */}
      <section className="home-section" style={{background:"var(--cream)"}}>
        <div style={{textAlign:"center",marginBottom:48}}>
          <span className="section-tag">{t("sections.explore")}</span>
          <h2 className="section-title">{t("sections.topDest")}</h2>
          <div className="gold-divider" style={{margin:"16px auto 0"}} />
        </div>
        <div className="destinations-grid">
          {destinations.map((d, i) => (
            <div key={i} className="dest-card" onClick={() => go("/destinations")}>
              <img
                src={d.img || FALLBACK_DESTINATION_IMGS[i]}
                alt={d.name}
                loading="lazy"
                onError={(event) => {
                  if (event.currentTarget.src !== FALLBACK_DESTINATION_IMGS[i]) {
                    event.currentTarget.src = FALLBACK_DESTINATION_IMGS[i];
                  }
                }}
              />
              <div className="dest-overlay">
                <div className="dest-tag">{d.tag}</div>
                <div className="dest-name">{d.name}</div>
              </div>
              <div className="dest-discount-badge">{d.discount}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PACKAGES */}
      <section className="home-section" style={{background:"white"}}>
        <div style={{textAlign:"center",marginBottom:48}}>
          <span className="section-tag">{t("sections.bestValue")}</span>
          <h2 className="section-title">{t("sections.popularPkg")}</h2>
          <div className="gold-divider" style={{margin:"16px auto 0"}} />
        </div>
        <div className="packages-grid">
          {packages.slice(0,3).map((p, i) => (
            <div key={i} className="pkg-card" onClick={() => go("/packages")}>
              <div className="pkg-img">
                <img src={p.img || FALLBACK_PROGRAMME_IMGS[i]} alt={p.title} loading="lazy" />
                <div className="pkg-type-badge">{p.type}</div>
              </div>
              <div className="pkg-body">
                <div className="pkg-meta"><span>🗓 {p.days} {t("common.days")}</span><span>👥 {p.people}</span></div>
                <div className="pkg-title">{p.title}</div>
                <div className="pkg-location">📍 {p.location || p.loc}</div>
                <div className="pkg-price-row">
                  <div>
                    <div className="pkg-old-price">{p.old} MAD</div>
                    <span className="pkg-new-price">{p.price}</span>
                    <span style={{fontSize:12,color:"var(--text-muted)",marginInlineStart:4}}>MAD</span>
                  </div>
                  <button className="btn-gold" style={{padding:"10px 20px",fontSize:13}} onClick={e=>{e.stopPropagation();go("/contact")}}>{t("nav.bookNow")}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{textAlign:"center",marginTop:40}}>
          <button className="btn-outline" onClick={() => go("/packages")}>{t("sections.viewAll")}</button>
        </div>
      </section>

      {/* WHY US */}
      <section className="home-section" style={{background:"var(--cream)"}}>
        <div className="why-grid">
          <div>
            <span className="section-tag">{t("sections.whyTag")}</span>
            <h2 className="section-title">{t("sections.whyTitle")}</h2>
            <div className="gold-divider" />
            <p className="section-subtitle">{t("sections.whySub")}</p>
          </div>
          <div className="why-cards">
            {whyCards.map(([icon,title,text], i) => (
              <div key={i} className="why-card">
                <div style={{fontSize:28,marginBottom:8}}>{icon}</div>
                <div style={{fontFamily:"var(--font-heading)",fontSize:16,fontWeight:600,marginBottom:6,color:"var(--deep)"}}>{title}</div>
                <div style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.6}}>{text}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="features-grid">
          {featureCards.map(([icon,title,text], i) => (
            <div key={i} className="feature-card">
              <div className="feature-icon">{icon}</div>
              <div className="feature-title">{title}</div>
              <p className="feature-text">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="home-section" style={{background:"white"}}>
        <div style={{textAlign:"center",marginBottom:48}}>
          <span className="section-tag">{t("sections.reviews")}</span>
          <h2 className="section-title">{t("sections.reviewsTitle")}</h2>
          <div className="gold-divider" style={{margin:"16px auto 0"}} />
        </div>
        <div className="testi-grid">
          {testimonials.map((tt, i) => (
            <div key={i} className="testi-card">
              <div className="testi-stars">{"★".repeat(tt.rating)}</div>
              <p className="testi-text">"{tt.text}"</p>
              <div className="testi-author">
                <div className="testi-avatar">{tt.name[0]}</div>
                <div>
                  <div style={{fontWeight:600,fontSize:14,color:"var(--deep)"}}>{tt.name}</div>
                  <div style={{fontSize:12,color:"var(--text-muted)"}}>{tt.city}, {t("common.countryMorocco")}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="home-section" style={{background:"var(--cream)"}}>
        <div className="cta-banner">
          <div>
            <h2 className="cta-title">{t("sections.ctaTag")}</h2>
            <p className="cta-sub">{t("sections.ctaSub")}</p>
            <div className="cta-btns">
              <button className="btn-gold" onClick={() => go("/contact")}>{t("sections.ctaBtn")}</button>
              <a href="tel:+212665009314" className="btn-outline" style={{color:"var(--gold-light)",borderColor:"rgba(201,168,76,0.4)"}}>{t("sections.ctaCall")}</a>
            </div>
          </div>
          <div style={{textAlign:"right",fontSize:80,lineHeight:1}}>🕌</div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
